import { createContext, useContext, useEffect, useState } from "react";
import constate from "constate";
import { useResponsive } from "@introist/react-foundation/v2";

const readCollapsed = (key?: string) => {
  if (!key) return undefined;
  try {
    return localStorage.getItem(key) === "true";
  } catch {
    return undefined;
  }
};

const writeCollapsed = (key: string, collapsed: boolean) => {
  try {
    localStorage.setItem(key, String(collapsed));
  } catch {
    // Storage blocked (private mode etc.): the menu just doesn't remember its state.
  }
};

// Whether the side menu is collapsed. Remembered per browser on desktop; always collapsed on
// mobile.
export const [AppMenuContextProvider, useAppMenuContext] = constate(
  ({ localStorageKey }: { localStorageKey?: string }) => {
    const { isMobileLayout } = useResponsive();

    const [collapsed, setCollapsed] = useState(
      isMobileLayout ? true : readCollapsed(localStorageKey)
    );

    const toggleMenuCollapsed = () => {
      const next = !collapsed;
      setCollapsed(next);
      if (localStorageKey) writeCollapsed(localStorageKey, next);
    };

    useEffect(() => {
      setCollapsed(isMobileLayout ? true : readCollapsed(localStorageKey));
    }, [isMobileLayout, localStorageKey]);

    return { collapsed, setCollapsed, toggleMenuCollapsed };
  }
);

// The collapsed state as rendered by AppMenuShell, for its own items.
export const MenuCollapsedContext = createContext(false);
export const useMenuCollapsed = () => useContext(MenuCollapsedContext);
